import React, { useState, useEffect, useCallback } from 'react';
import { collection, getDocs, doc, getDoc, setDoc, collectionGroup, query, where, getCountFromServer } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { db, storage } from '../firebase';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { addDoc } from 'firebase/firestore';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import './Signups.css';

const Signups = () => {
  const [metrics, setMetrics] = useState({
    dau: 0,
    mau: 0,
    signupsToday: 0,
    signupsLast7Days: 0,
    signupsLast30Days: 0
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [generatingPDF, setGeneratingPDF] = useState(false);
  const [pdfPreview, setPdfPreview] = useState(null);
  const [pdfBlob, setPdfBlob] = useState(null);
  const [shareableLink, setShareableLink] = useState(null);
  const [trackingLink, setTrackingLink] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [trackingId, setTrackingId] = useState(null);

  const loadCachedMetrics = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      // Load cached metrics from analytics/signups document
      const signupsDocRef = doc(db, "analytics", "signups");
      const signupsDoc = await getDoc(signupsDocRef);

      if (signupsDoc.exists()) {
        const data = signupsDoc.data();
        setMetrics({
          dau: data.dau || 0,
          mau: data.mau || 0,
          signupsToday: data.signupsToday || 0,
          signupsLast7Days: data.signupsLast7Days || 0,
          signupsLast30Days: data.signupsLast30Days || 0,
        });
        setLastUpdated(data.lastUpdated?.toDate() || null);
        console.log("Loaded cached signups metrics from:", data.lastUpdated?.toDate());
      } else {
        console.log("No cached metrics found, calculating fresh...");
        await calculateAndSaveMetrics();
      }
    } catch (err) {
      console.error("Error loading cached metrics:", err);
      setError("Failed to load metrics. Please try again.");
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    loadCachedMetrics();
  }, [loadCachedMetrics]);

  const handleRefresh = async () => {
    setIsRefreshing(true);
    await calculateAndSaveMetrics();
    setIsRefreshing(false);
  };

  const calculateAndSaveMetrics = async () => {
    try {
      if (!isRefreshing) {
        setLoading(true);
      }
      setError(null);

      // Get current time boundaries for signup metrics
      const now = new Date();
      const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      const last7DaysStart = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
      const last30DaysStart = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);

      // Fetch DAU and MAU from Cloud Function (using GA4 first_open events)
      const functions = getFunctions();
      const getAnalyticsMetrics = httpsCallable(functions, 'getAnalyticsMetrics');

      let dau = 0;
      let mau = 0;

      try {
        const analyticsResult = await getAnalyticsMetrics();
        dau = analyticsResult.data.dau || 0;
        mau = analyticsResult.data.mau || 0;
      } catch (analyticsError) {
        console.error('Error fetching analytics from Cloud Function:', analyticsError);
        // Continue with signup metrics even if analytics fails
      }

      // Fetch signup metrics from Firestore (based on user createdAt)
      const usersRef = collection(db, 'users');
      const usersSnapshot = await getDocs(usersRef);

      let signupsToday = 0;
      let signupsLast7Days = 0;
      let signupsLast30Days = 0;

      usersSnapshot.forEach(doc => {
        const userData = doc.data();

        // Check for signups
        if (userData.createdAt) {
          const createdAt = userData.createdAt.toDate();

          if (createdAt >= todayStart) {
            signupsToday++;
          }
          if (createdAt >= last7DaysStart) {
            signupsLast7Days++;
          }
          if (createdAt >= last30DaysStart) {
            signupsLast30Days++;
          }
        }
      });

      const metricsData = {
        dau,
        mau,
        signupsToday,
        signupsLast7Days,
        signupsLast30Days,
      };

      setMetrics(metricsData);
      setLastUpdated(now);

      // Save metrics to analytics/signups document
      const signupsDocRef = doc(db, "analytics", "signups");
      await setDoc(signupsDocRef, {
        ...metricsData,
        lastUpdated: now,
      });

      console.log("✅ Signups metrics saved to analytics/signups");
    } catch (err) {
      console.error('Error calculating metrics:', err);
      setError('Failed to calculate metrics. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="signups-page">
        <div className="page-header">
          <h1>Signups & Activity</h1>
        </div>
        <div className="loading-state">
          <div className="spinner"></div>
          <p>Loading metrics...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="signups-page">
        <div className="page-header">
          <h1>Signups & Activity</h1>
        </div>
        <div className="error-state">
          <p>{error}</p>
          <button onClick={handleRefresh} className="retry-button">
            Retry
          </button>
        </div>
      </div>
    );
  }

  const formatLastUpdated = () => {
    if (!lastUpdated) return "";

    const now = new Date();
    const diffMs = now - lastUpdated;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return "just now";
    if (diffMins < 60) return `${diffMins} minute${diffMins === 1 ? '' : 's'} ago`;
    if (diffHours < 24) return `${diffHours} hour${diffHours === 1 ? '' : 's'} ago`;
    return `${diffDays} day${diffDays === 1 ? '' : 's'} ago`;
  };

  const getReportStats = async () => {
    try {
      setGeneratingPDF(true);
      const now = new Date();
      const thirtyDaysAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
      const sixtyDaysAgo = new Date(now.getTime() - 60 * 24 * 60 * 60 * 1000);

      // Get MAU and signups (already calculated)
      const mau = metrics.mau;
      const signupsLast30Days = metrics.signupsLast30Days;

      // Get number of schools represented
      const usersRef = collection(db, 'users');
      const usersWithSchoolQuery = query(usersRef, where("school", ">", ""));
      const usersWithSchoolSnapshot = await getDocs(usersWithSchoolQuery);
      
      const schoolSet = new Set();
      usersWithSchoolSnapshot.forEach((userDoc) => {
        const userData = userDoc.data();
        const schoolName = userData.school?.trim() || "";
        if (schoolName) {
          schoolSet.add(schoolName);
        }
      });
      const numberOfSchools = schoolSet.size;

      // Get all assignments and calculate average grade and total count
      const assignmentsRef = collectionGroup(db, "assignments");
      const assignmentsSnapshot = await getDocs(assignmentsRef);

      let totalAssignments = 0;
      let gradedAssignments = 0;
      let totalGrade = 0;

      assignmentsSnapshot.forEach((assignmentDoc) => {
        totalAssignments++;
        const assignmentData = assignmentDoc.data();
        const grade = assignmentData.grade;
        if (grade !== undefined && grade !== null && typeof grade === 'number' && grade >= 0) {
          gradedAssignments++;
          totalGrade += grade;
        }
      });

      const averageGrade = gradedAssignments > 0 ? Math.round((totalGrade / gradedAssignments) * 100) / 100 : 0;

      // Calculate revenue % increase last 30 days
      // Get current revenue from Revenue page logic
      const functions = getFunctions();
      let currentRevenue = 0;
      let revenue30DaysAgo = 0;

      try {
        // Get current revenue
        const usersSnapshot = await getDocs(usersRef);
        let premiumCount = 0;
        let scholarCount = 0;
        let premiumCount30DaysAgo = 0;
        let scholarCount30DaysAgo = 0;

        usersSnapshot.forEach((userDoc) => {
          const userData = userDoc.data();
          const subscriptionStatus = userData.subscriptionStatus;
          const subscriptionSource = userData.subscriptionSource;
          const createdAt = userData.createdAt?.toDate?.() || userData.createdAt;

          // Skip organization subscriptions
          if (subscriptionSource === "organization") {
            return;
          }

          // Count current subscriptions
          if (subscriptionStatus === "active") {
            premiumCount++;
            // If user existed 30 days ago, count them for 30 days ago revenue
            if (createdAt && createdAt < thirtyDaysAgo) {
              premiumCount30DaysAgo++;
            }
          } else if (subscriptionStatus === "scholar") {
            scholarCount++;
            // If user existed 30 days ago, count them for 30 days ago revenue
            if (createdAt && createdAt < thirtyDaysAgo) {
              scholarCount30DaysAgo++;
            }
          }
        });

        // Calculate MRR
        currentRevenue = premiumCount * 4.99 + scholarCount * 9.99;
        revenue30DaysAgo = premiumCount30DaysAgo * 4.99 + scholarCount30DaysAgo * 9.99;

        // Get organization revenue
        try {
          const getOrganizationRevenue = httpsCallable(functions, 'getOrganizationRevenue');
          const orgRevenueResult = await getOrganizationRevenue();
          const orgRevenueData = orgRevenueResult.data;
          const orgMRR = orgRevenueData.totalOrgMRR || 0;
          currentRevenue += orgMRR;
          
          // For org revenue 30 days ago, we'll estimate based on current (simplified)
          // In a real scenario, you'd need historical data
          revenue30DaysAgo += orgMRR * 0.9; // Estimate 90% of current (conservative)
        } catch (orgError) {
          console.error('Error fetching organization revenue:', orgError);
        }
      } catch (revenueError) {
        console.error('Error calculating revenue:', revenueError);
      }

      // Calculate revenue % increase
      let revenueIncreasePercent = 0;
      if (revenue30DaysAgo > 0) {
        revenueIncreasePercent = Math.round(((currentRevenue - revenue30DaysAgo) / revenue30DaysAgo) * 100);
      } else if (currentRevenue > 0) {
        revenueIncreasePercent = 100; // New revenue
      }

      return {
        mau,
        signupsLast30Days,
        numberOfSchools,
        averageGrade,
        totalAssignments,
        revenueIncreasePercent,
        currentRevenue,
        revenue30DaysAgo,
      };
    } catch (err) {
      console.error("Error getting report stats:", err);
      throw err;
    }
  };

  const generateReportPDF = async () => {
    try {
      const stats = await getReportStats();

      // Create a temporary div to render the PDF content
      const pdfContainer = document.createElement("div");
      pdfContainer.style.position = "absolute";
      pdfContainer.style.left = "-9999px";
      pdfContainer.style.width = "210mm"; // A4 width
      pdfContainer.style.padding = "18mm";
      pdfContainer.style.backgroundColor = "#ffffff";
      pdfContainer.style.fontFamily = "Arial, sans-serif";
      pdfContainer.style.color = "#1a1a1a";
      document.body.appendChild(pdfContainer);

      // Create PDF content - optimized for 75-85% of page
      const logoPath = window.location.origin + "/textLogo.png";
      pdfContainer.innerHTML = `
        <div style="display: flex; flex-direction: column; gap: 20px; height: 100%;">
          <!-- Header with Logo -->
          <div style="display: flex; justify-content: space-between; align-items: center; border-bottom: 3px solid #238a94; padding-bottom: 15px;">
            <div style="display: flex; align-items: center; gap: 10px;">
              <img src="${logoPath}" alt="SyllySync" style="height: 45px; object-fit: contain; max-width: 180px;" onerror="this.style.display='none'; this.nextElementSibling.style.display='block';">
              <div style="display: none; font-size: 22px; font-weight: 700; color: #238a94;">SyllySync</div>
            </div>
            <div style="text-align: right; color: #666; font-size: 12px;">
              <div>Application-Wide Performance Report</div>
              <div style="margin-top: 4px;">${new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</div>
            </div>
          </div>

          <!-- Title Header -->
          <div style="text-align: center; padding: 15px 0;">
            <h1 style="margin: 0; font-size: 30px; color: #238a94; font-weight: 700;">SyllySync Platform Overview</h1>
            <div style="margin-top: 6px; font-size: 16px; color: #666; font-weight: 600;">
              Comprehensive Analytics Report
            </div>
          </div>

          <!-- Key Metrics Grid -->
          <div style="display: grid; grid-template-columns: repeat(3, 1fr); gap: 15px; margin-top: 10px;">
            <div style="background: linear-gradient(135deg, #238a94 0%, #1d6f77 100%); padding: 20px; border-radius: 10px; color: white;">
              <div style="font-size: 11px; opacity: 0.9; margin-bottom: 8px;">Monthly Active Users</div>
              <div style="font-size: 38px; font-weight: 700; line-height: 1;">${stats.mau.toLocaleString()}</div>
              <div style="font-size: 10px; opacity: 0.8; margin-top: 6px;">Last 30 days</div>
            </div>

            <div style="background: linear-gradient(135deg, #FF914D 0%, #e67e3d 100%); padding: 20px; border-radius: 10px; color: white;">
              <div style="font-size: 11px; opacity: 0.9; margin-bottom: 8px;">New Signups</div>
              <div style="font-size: 38px; font-weight: 700; line-height: 1;">${stats.signupsLast30Days.toLocaleString()}</div>
              <div style="font-size: 10px; opacity: 0.8; margin-top: 6px;">Last 30 days</div>
            </div>

            <div style="background: linear-gradient(135deg, #4caf50 0%, #45a049 100%); padding: 20px; border-radius: 10px; color: white;">
              <div style="font-size: 11px; opacity: 0.9; margin-bottom: 8px;">Schools Represented</div>
              <div style="font-size: 38px; font-weight: 700; line-height: 1;">${stats.numberOfSchools.toLocaleString()}</div>
              <div style="font-size: 10px; opacity: 0.8; margin-top: 6px;">Unique institutions</div>
            </div>

            <div style="background: linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%); padding: 20px; border-radius: 10px; color: white;">
              <div style="font-size: 11px; opacity: 0.9; margin-bottom: 8px;">Average Grade</div>
              <div style="font-size: 38px; font-weight: 700; line-height: 1;">${stats.averageGrade.toFixed(1)}%</div>
              <div style="font-size: 10px; opacity: 0.8; margin-top: 6px;">Across graded assignments</div>
            </div>

            <div style="background: linear-gradient(135deg, #ec4899 0%, #db2777 100%); padding: 20px; border-radius: 10px; color: white;">
              <div style="font-size: 11px; opacity: 0.9; margin-bottom: 8px;">Assignments Tracked</div>
              <div style="font-size: 38px; font-weight: 700; line-height: 1;">${stats.totalAssignments.toLocaleString()}</div>
              <div style="font-size: 10px; opacity: 0.8; margin-top: 6px;">Total assignments</div>
            </div>

            <div style="background: linear-gradient(135deg, #14b8a6 0%, #0d9488 100%); padding: 20px; border-radius: 10px; color: white;">
              <div style="font-size: 11px; opacity: 0.9; margin-bottom: 8px;">Revenue Growth</div>
              <div style="font-size: 38px; font-weight: 700; line-height: 1;">${stats.revenueIncreasePercent >= 0 ? '+' : ''}${stats.revenueIncreasePercent}%</div>
              <div style="font-size: 10px; opacity: 0.8; margin-top: 6px;">vs 30 days ago</div>
            </div>
          </div>

          <!-- Insights Section -->
          <div style="background: #f8f9fa; padding: 18px; border-radius: 10px; border-left: 5px solid #238a94; margin-top: 12px;">
            <h3 style="margin: 0 0 12px 0; font-size: 16px; color: #238a94; font-weight: 700;">Platform Insights</h3>
            <div style="color: #333; line-height: 1.6; font-size: 12px;">
              <p style="margin: 0 0 10px 0;">
                SyllySync continues to demonstrate strong growth with <strong>${stats.mau.toLocaleString()} monthly active users</strong> and 
                <strong> ${stats.signupsLast30Days.toLocaleString()} new signups</strong> in the last 30 days, reflecting increasing adoption 
                and engagement across the platform.
              </p>
              <p style="margin: 0 0 10px 0;">
                The platform now serves <strong>${stats.numberOfSchools.toLocaleString()} unique educational institutions</strong>, 
                demonstrating broad reach and institutional adoption. Students have tracked <strong>${stats.totalAssignments.toLocaleString()} assignments</strong> 
                across all courses, with an average grade of <strong>${stats.averageGrade.toFixed(1)}%</strong> among graded assignments, 
                showcasing the platform's effectiveness in academic management.
              </p>
              <p style="margin: 0;">
                Revenue has shown <strong>${stats.revenueIncreasePercent >= 0 ? 'positive' : 'negative'} growth of ${Math.abs(stats.revenueIncreasePercent)}%</strong> 
                compared to 30 days ago, indicating ${stats.revenueIncreasePercent >= 0 ? 'strong' : 'developing'} financial performance and 
                sustainable business growth.
              </p>
            </div>
          </div>

          <!-- Footer -->
          <div style="margin-top: auto; padding-top: 12px; border-top: 2px solid #e5e7eb; text-align: center; color: #666; font-size: 10px;">
            <div>Generated by SyllySync Admin Dashboard</div>
            <div style="margin-top: 4px;">For more information, contact your SyllySync representative</div>
          </div>
        </div>
      `;

      // Wait for image to load and ensure it's rendered
      const logoImg = pdfContainer.querySelector('img');
      if (logoImg) {
        await new Promise((resolve) => {
          if (logoImg.complete) {
            setTimeout(resolve, 300);
          } else {
            logoImg.onload = () => setTimeout(resolve, 300);
            logoImg.onerror = () => setTimeout(resolve, 300);
            // Timeout after 2 seconds
            setTimeout(resolve, 2000);
          }
        });
      } else {
        await new Promise((resolve) => setTimeout(resolve, 300));
      }

      // Convert to canvas then PDF
      const canvas = await html2canvas(pdfContainer, {
        scale: 2,
        useCORS: true,
        logging: false,
        backgroundColor: "#ffffff",
      });

      const imgData = canvas.toDataURL("image/png");
      const pdf = new jsPDF("p", "mm", "a4");
      const imgWidth = 210; // A4 width in mm
      const pageHeight = 297; // A4 height in mm
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      
      // Scale to fit on single page if content is too tall
      let finalWidth = imgWidth;
      let finalHeight = imgHeight;
      
      if (imgHeight > pageHeight) {
        // Scale down to fit on one page
        const scale = pageHeight / imgHeight;
        finalWidth = imgWidth * scale;
        finalHeight = pageHeight;
      }

      // Add image to single page only
      pdf.addImage(imgData, "PNG", (imgWidth - finalWidth) / 2, 0, finalWidth, finalHeight);

      // Clean up
      document.body.removeChild(pdfContainer);

      // Generate PDF blob for preview
      const pdfBlobData = pdf.output("blob");
      const pdfUrl = URL.createObjectURL(pdfBlobData);

      // Store blob for potential upload
      setPdfBlob(pdfBlobData);

      // Show preview modal
      setPdfPreview(pdfUrl);
      setShareableLink(null); // Reset shareable link

      setGeneratingPDF(false);
    } catch (err) {
      console.error("Error generating PDF:", err);
      alert("Error generating PDF: " + (err.message || "Unknown error"));
      setGeneratingPDF(false);
    }
  };

  const handleDownloadPDF = () => {
    if (pdfPreview) {
      const link = document.createElement("a");
      link.href = pdfPreview;
      link.download = `SyllySync_Platform_Report_${new Date().toISOString().split('T')[0]}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  const handleClosePreview = () => {
    if (pdfPreview) {
      URL.revokeObjectURL(pdfPreview);
      setPdfPreview(null);
      setPdfBlob(null);
      setShareableLink(null);
      setTrackingLink(null);
      setTrackingId(null);
    }
  };

  const handleSaveToFirebase = async () => {
    if (!pdfBlob) {
      alert("No PDF available to upload");
      return;
    }

    try {
      setIsUploading(true);
      setError(null);

      // Create a sanitized filename
      const timestamp = new Date().toISOString().replace(/[:.]/g, "-");
      const fileName = `platform-reports/SyllySync_Platform_Report_${timestamp}.pdf`;

      // Create storage reference
      const storageRef = ref(storage, fileName);

      // Upload PDF blob
      await uploadBytes(storageRef, pdfBlob, {
        contentType: "application/pdf",
      });

      // Get download URL
      const downloadURL = await getDownloadURL(storageRef);

      // Get current stats to store for comparison
      const statsForHash = await getReportStats();
      const statsHash = JSON.stringify({
        mau: statsForHash.mau,
        signupsLast30Days: statsForHash.signupsLast30Days,
        numberOfSchools: statsForHash.numberOfSchools,
        averageGrade: statsForHash.averageGrade,
        totalAssignments: statsForHash.totalAssignments,
        revenueIncreasePercent: statsForHash.revenueIncreasePercent,
      });

      // Prompt for email (optional) for tracking
      const email = prompt("Enter recipient email for tracking (optional, can be left blank):") || null;

      // Create tracking document in Firestore
      const trackingDocRef = await addDoc(collection(db, "pdfTracking"), {
        reportType: "platform",
        fileName: fileName,
        storageUrl: downloadURL,
        statsHash: statsHash,
        email: email,
        createdAt: new Date(),
        views: [],
        totalViews: 0,
        uniqueViews: 0,
        lastViewed: null,
      });

      const trackingId = trackingDocRef.id;

      // Generate tracking URL (using the admin dashboard URL as base)
      const baseUrl = window.location.origin;
      const trackingUrl = email 
        ? `${baseUrl}/track-pdf/${trackingId}?email=${encodeURIComponent(email)}`
        : `${baseUrl}/track-pdf/${trackingId}`;

      setTrackingId(trackingId);
      setShareableLink(downloadURL);
      setTrackingLink(trackingUrl);
      alert("PDF uploaded successfully! Tracking link generated. Use the tracking link for emails.");
    } catch (err) {
      console.error("Error uploading PDF:", err);
      setError("Failed to upload PDF. Please try again.");
      alert("Error uploading PDF: " + (err.message || "Unknown error"));
    } finally {
      setIsUploading(false);
    }
  };

  const handleCopyLink = (linkType = 'tracking') => {
    const linkToCopy = linkType === 'tracking' ? trackingLink : shareableLink;
    if (linkToCopy) {
      navigator.clipboard.writeText(linkToCopy).then(() => {
        alert(`${linkType === 'tracking' ? 'Tracking' : 'Direct'} link copied to clipboard!`);
      }).catch((err) => {
        console.error("Failed to copy link:", err);
        alert("Failed to copy link. Please copy manually.");
      });
    }
  };

  return (
    <div className="signups-page">
      <div className="page-header">
        <div>
          <h1>Signups & Activity</h1>
          {lastUpdated && (
            <p className="last-updated">Last updated: {formatLastUpdated()}</p>
          )}
        </div>
        <div style={{ display: 'flex', gap: '12px' }}>
          <button
            onClick={generateReportPDF}
            className="refresh-button"
            disabled={generatingPDF}
            style={{ background: '#238a94', color: 'white', border: 'none' }}
          >
            {generatingPDF ? '⏳ Generating PDF...' : '📄 Generate Report'}
          </button>
          <button
            onClick={handleRefresh}
            className="refresh-button"
            disabled={isRefreshing}
          >
            {isRefreshing ? '⏳ Refreshing...' : '🔄 Refresh'}
          </button>
        </div>
      </div>

      <div className="metrics-grid">
        <MetricCard
          title="Daily Active Users"
          value={metrics.dau}
          description="Users active today"
          color="#2AB3C0"
          icon="👤"
        />
        <MetricCard
          title="Monthly Active Users"
          value={metrics.mau}
          description="Users active in last 30 days"
          color="#FF914D"
          icon="👥"
        />
        <MetricCard
          title="Signups Today"
          value={metrics.signupsToday}
          description="New users today"
          color="#10b981"
          icon="✨"
        />
        <MetricCard
          title="Signups (7 Days)"
          value={metrics.signupsLast7Days}
          description="New users in last 7 days"
          color="#2AB3C0"
          icon="📈"
        />
        <MetricCard
          title="Signups (30 Days)"
          value={metrics.signupsLast30Days}
          description="New users in last 30 days"
          color="#FF914D"
          icon="📊"
        />
      </div>

      {/* PDF Preview Modal */}
      {pdfPreview && (
        <div className="pdf-preview-modal" onClick={handleClosePreview}>
          <div className="pdf-preview-content" onClick={(e) => e.stopPropagation()}>
            <div className="pdf-preview-header">
              <h2>Platform Report Preview</h2>
              <div className="pdf-preview-actions">
                <button onClick={handleDownloadPDF} className="pdf-download-button">
                  💾 Download PDF
                </button>
                <button 
                  onClick={handleSaveToFirebase} 
                  className="pdf-upload-button"
                  disabled={isUploading || shareableLink !== null}
                >
                  {isUploading ? "⏳ Uploading..." : shareableLink ? "✅ Uploaded" : "☁️ Save to Firebase"}
                </button>
                {trackingLink && (
                  <button onClick={() => handleCopyLink('tracking')} className="pdf-copy-button">
                    📋 Copy Tracking Link
                  </button>
                )}
                {shareableLink && !trackingLink && (
                  <button onClick={() => handleCopyLink('direct')} className="pdf-copy-button">
                    📋 Copy Link
                  </button>
                )}
                <button onClick={handleClosePreview} className="pdf-close-button">
                  ✕ Close
                </button>
              </div>
            </div>
            {trackingLink && (
              <div className="pdf-shareable-link-container" style={{ background: '#eff6ff', borderColor: '#3b82f6' }}>
                <div className="pdf-shareable-link-label" style={{ color: '#1e40af', fontWeight: 700 }}>
                  📧 Tracking Link (Use for Emails):
                </div>
                <div className="pdf-shareable-link-wrapper">
                  <input 
                    type="text" 
                    value={trackingLink} 
                    readOnly 
                    className="pdf-shareable-link-input"
                    onClick={(e) => e.target.select()}
                    style={{ borderColor: '#3b82f6' }}
                  />
                  <button onClick={() => handleCopyLink('tracking')} className="pdf-copy-link-button" style={{ background: '#3b82f6' }}>
                    📋 Copy
                  </button>
                </div>
                <div style={{ marginTop: '8px', fontSize: '12px', color: '#1e40af' }}>
                  This link tracks opens, time viewed, and repeat views
                </div>
              </div>
            )}
            {shareableLink && (
              <div className="pdf-shareable-link-container">
                <div className="pdf-shareable-link-label">Direct Link (No Tracking):</div>
                <div className="pdf-shareable-link-wrapper">
                  <input 
                    type="text" 
                    value={shareableLink} 
                    readOnly 
                    className="pdf-shareable-link-input"
                    onClick={(e) => e.target.select()}
                  />
                  <button onClick={() => handleCopyLink('direct')} className="pdf-copy-link-button">
                    📋 Copy
                  </button>
                </div>
              </div>
            )}
            <div className="pdf-preview-iframe-container">
              <iframe
                src={pdfPreview}
                title="PDF Preview"
                className="pdf-preview-iframe"
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const MetricCard = ({ title, value, description, color, icon }) => {
  return (
    <div className="metric-card" style={{ borderTopColor: color }}>
      <div className="metric-icon" style={{ background: `${color}20`, color }}>
        {icon}
      </div>
      <div className="metric-content">
        <h3 className="metric-title">{title}</h3>
        <div className="metric-value" style={{ color }}>
          {value.toLocaleString()}
        </div>
        <p className="metric-description">{description}</p>
      </div>
    </div>
  );
};

export default Signups;
